import React, { useMemo, useState, useEffect } from "react";
import { api } from "../api/client";
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Checkbox } from "./ui/checkbox";
import { Interruttore, Ricerca } from "./integrations/casella";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  MoreVertical,
  RefreshCw,
  Trash2,
  Pencil,
  Eye,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ExternalLink,
} from "lucide-react";

const TYPE_TABS = [
  { value: "all", label: "Tutte" },
  { value: "url", label: "URL" },
  { value: "sitemap", label: "Sitemap" },
  { value: "pdf", label: "PDF" },
  { value: "text", label: "Testo" },
  { value: "faq", label: "FAQ" },
  { value: "product", label: "Prodotti" },
];

const TIPO_BREVE = { url: "URL", sitemap: "Sitemap", pdf: "PDF", faq: "FAQ" };

function formatDateTime(d) {
  if (!d) return "";
  const dt = new Date(d);
  return isNaN(dt.getTime())
    ? ""
    : dt.toLocaleString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
}

function StatusBadge({ status, t }) {
  const map = {
    ready: { label: t("sources.statusReady", "Pronto"), dot: "bg-[#19D163]", cls: "bg-[#19D163]/10 text-[#0F7A3D]" },
    processing: { label: t("sources.statusProcessing", "In corso"), dot: "bg-blue-500 animate-pulse", cls: "bg-blue-50 text-blue-700" },
    pending: { label: t("sources.statusPending", "In attesa"), dot: "bg-amber-500", cls: "bg-amber-50 text-amber-800" },
    error: { label: t("sources.statusError", "Errore"), dot: "bg-red-500", cls: "bg-red-50 text-red-700" },
  };
  const s = map[status] || { label: status || "—", dot: "bg-[#94A3B8]", cls: "bg-[#F1F5F9] text-[#475569]" };
  // Fixed min width + centered so changing status (Pronto ↔ In attesa ↔ In corso)
  // doesn't resize the column and shift the layout.
  return (
    <span
      className={`inline-flex min-w-[92px] items-center justify-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${s.cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} aria-hidden="true" />
      {s.label}
    </span>
  );
}

/**
 * Sources list as a DataTable (tabs by type + search + pagination), with a modal
 * to READ a source. Fully CONTROLLED / SERVER-DRIVEN: pagination, search, type-tab
 * filtering and per-type counts all come from the parent (server-side). The table
 * only renders the current page of rows; it does no client-side filtering/paging.
 */
export default function SourcesDataTable({
  sources = [], // current page rows only
  // server pagination (controlled)
  pageIndex = 0,
  pageSize = 20,
  pageSizes = [],
  onPageSizeChange = () => {},
  pageCount = 1,
  totalCount = 0,
  onPageChange = () => {},
  // controlled search + tab + counts
  search = "",
  onSearchChange = () => {},
  tab = "all",
  onTabChange = () => {},
  status = "",
  onStatusChange = () => {},
  counts = {},
  isLoading = false,
  // display + actions
  getTypeIcon,
  getSourceDisplayName,
  getSourceSubtitle,
  onReprocess,
  onDelete,
  onEditText,
  onEditFaq,
  onEditProduct,
  reprocessing = false,
  // selection (TanStack rowSelection, keyed by _id)
  rowSelection = {},
  setRowSelection,
  t = (k, d) => d || k,
}) {
  const [sorting, setSorting] = useState([]);
  const [viewSource, setViewSource] = useState(null);

  // Local mirror of the search box for snappy typing; debounced upstream so we
  // don't refetch on every keystroke.
  const [searchInput, setSearchInput] = useState(search);
  useEffect(() => {
    setSearchInput(search);
  }, [search]);
  useEffect(() => {
    const id = setTimeout(() => {
      if (searchInput !== search) onSearchChange(searchInput);
    }, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const columns = useMemo(
    () => [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={
              table.getIsAllRowsSelected()
                ? true
                : table.getIsSomeRowsSelected()
                ? "indeterminate"
                : false
            }
            onCheckedChange={(value) => table.toggleAllRowsSelected(!!value)}
            aria-label={t("sources.selectAll", "Seleziona tutto")}
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label={t("sources.selectRow", "Seleziona riga")}
          />
        ),
      },
      {
        accessorKey: "type",
        header: t("sources.colType", "Tipo"),
        cell: ({ row }) => (
          <div className="flex items-center gap-2.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#F1F5F9]">
              {getTypeIcon(row.original)}
            </span>
            <span className="text-xs text-[#64748B]">
              {TIPO_BREVE[row.original.type] ||
                t(`sources.type_${row.original.type}`, row.original.type)}
            </span>
          </div>
        ),
      },
      {
        id: "name",
        header: t("sources.colName", "Nome"),
        cell: ({ row }) => {
          const fuori = row.original.type === "sitemap" ? row.original.pagine_oltre_il_piano || 0 : 0;
          return (
            <div className="min-w-0">
              {/* Nomi lunghi (URL): a capo dove serve, al massimo due righe; interi
                  al passaggio del mouse. Non allargano la tabella. */}
              <button
                type="button"
                title={getSourceDisplayName(row.original)}
                className="line-clamp-2 break-all text-left text-sm font-medium text-[#020617] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                onClick={() => setViewSource(row.original)}
              >
                {getSourceDisplayName(row.original)}
              </button>
              {/* Pagine della sitemap rimaste fuori: il piano non aveva piu' posto. */}
              {fuori > 0 ? (
                <p className="mt-0.5 text-xs text-[#B45309]">{t("sources.pagesOverPlan", { count: fuori })}</p>
              ) : null}
            </div>
          );
        },
      },
      {
        accessorKey: "status",
        header: t("sources.colStatus", "Stato"),
        cell: ({ row }) => <StatusBadge status={row.original.status} t={t} />,
      },
      {
        id: "detail",
        header: t("sources.colDetail", "Dettaglio"),
        cell: ({ row }) => {
          const s = row.original;
          const isAutoUpdate = s.type === "url" || s.type === "sitemap";
          const lastUpdate = isAutoUpdate
            ? formatDateTime(s.last_run || s.scheduling?.lastRunAt)
            : "";
          return (
            <div className="flex flex-col gap-0.5">
              <span className="text-xs text-[#64748B]">
                {getSourceSubtitle(s)}
              </span>
              {lastUpdate && (
                <span className="text-[11px] text-[#94A3B8]">
                  {t("sources.lastUpdate", "ultimo agg.: {{date}}", {
                    date: lastUpdate,
                  })}
                </span>
              )}
            </div>
          );
        },
      },
      {
        id: "actions",
        header: () => null,
        cell: ({ row }) => {
          const s = row.original;
          // Editable sources (text, manual FAQ, manual product) already re-index on
          // Edit → a separate "Riprocessa" is redundant; only offer "Riprova" on error.
          const isEditable =
            s.type === "text" ||
            (s.type === "faq" && s.faq_origin === "manual") ||
            (s.type === "product" && s.product_origin === "manual");
          return (
            <div className="flex justify-end">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    aria-label={t("sources.actions", "Azioni")}
                    className="flex h-8 w-8 items-center justify-center rounded-[8px] text-[#94A3B8] transition-colors hover:bg-[#F1F5F9] hover:text-[#334155] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44 rounded-xl">
                  <DropdownMenuItem onClick={() => setViewSource(s)}>
                    <Eye className="mr-2 h-4 w-4" /> {t("sources.read", "Leggi")}
                  </DropdownMenuItem>
                  {s.type === "text" && onEditText && (
                    <DropdownMenuItem
                      onClick={() => onEditText(s)}
                      disabled={s.status === "processing"}
                    >
                      <Pencil className="mr-2 h-4 w-4" />{" "}
                      {t("common.edit", "Modifica")}
                    </DropdownMenuItem>
                  )}
                  {s.type === "faq" && s.faq_origin === "manual" && onEditFaq && (
                    <DropdownMenuItem
                      onClick={() => onEditFaq(s)}
                      disabled={s.status === "processing"}
                    >
                      <Pencil className="mr-2 h-4 w-4" />{" "}
                      {t("common.edit", "Modifica")}
                    </DropdownMenuItem>
                  )}
                  {s.type === "product" &&
                    s.product_origin === "manual" &&
                    onEditProduct && (
                      <DropdownMenuItem
                        onClick={() => onEditProduct(s)}
                        disabled={s.status === "processing"}
                      >
                        <Pencil className="mr-2 h-4 w-4" />{" "}
                        {t("common.edit", "Modifica")}
                      </DropdownMenuItem>
                    )}
                  {onReprocess && (!isEditable || s.status === "error") && (
                    <DropdownMenuItem
                      onClick={() => onReprocess(s._id)}
                      disabled={reprocessing}
                    >
                      <RefreshCw className="mr-2 h-4 w-4" />{" "}
                      {isEditable
                        ? t("sources.retry", "Riprova")
                        : t("sources.reprocess", "Riprocessa")}
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={() => onDelete(s)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />{" "}
                    {t("common.delete", "Elimina")}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
      },
    ],
    [getTypeIcon, getSourceDisplayName, getSourceSubtitle, onReprocess, onDelete, onEditText, onEditFaq, onEditProduct, reprocessing, t]
  );

  const table = useReactTable({
    data: sources,
    columns,
    getRowId: (row) => row._id,
    enableRowSelection: true,
    manualPagination: true,
    pageCount: Math.max(1, pageCount),
    state: {
      sorting,
      rowSelection,
      pagination: { pageIndex, pageSize },
    },
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  const canPrev = pageIndex > 0 && !isLoading;
  const canNext = pageIndex + 1 < pageCount && !isLoading;

  return (
    <div className="flex w-full flex-col gap-4">
      {/* Barra: tipi (con i conteggi del server), stato e ricerca */}
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        {/* Sette tipi: sul telefono l'interruttore scorre di lato. */}
        <div className="-mx-1 overflow-x-auto px-1 pb-1 xl:pb-0">
          <Interruttore
            etichetta={t("sources.colType", "Tipo")}
            valore={tab}
            onCambia={onTabChange}
            voci={TYPE_TABS.map((tt) => ({
              valore: tt.value,
              etichetta: t(`sources.tab_${tt.value}`, tt.label),
              numero: counts[tt.value] || null,
            }))}
          />
        </div>
        <div className="flex w-full flex-col gap-2 sm:flex-row sm:items-center xl:w-auto">
          <Select value={status || "all"} onValueChange={onStatusChange}>
            <SelectTrigger
              aria-label={t("sources.colStatus", "Stato")}
              className="h-10 w-full rounded-lg border-black/10 bg-white/80 text-sm text-[#334155] focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-ring sm:w-44"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="rounded-xl">
              <SelectItem value="all">
                {t("sources.statusAll", "Tutti gli stati")}
              </SelectItem>
              <SelectItem value="ready">
                {t("sources.status_ready", "Pronto")}
              </SelectItem>
              <SelectItem value="processing">
                {t("sources.status_processing", "In corso")}
              </SelectItem>
              <SelectItem value="pending">
                {t("sources.status_pending", "In attesa")}
              </SelectItem>
              <SelectItem value="error">
                {t("sources.status_error", "Errore")}
              </SelectItem>
            </SelectContent>
          </Select>
          <Ricerca
            valore={searchInput}
            onCambia={setSearchInput}
            segnaposto={t("sources.searchPlaceholder", "Cerca per nome...")}
            etichetta={t("sources.searchPlaceholder", "Cerca per nome...")}
          />
        </div>
      </div>

      {/* Tabella e pagine nella stessa card */}
      <div className="overflow-hidden rounded-[14px] border border-[#E2E8F0] bg-white">
        <Table>
          <TableHeader className="bg-[#F8FAFC]">
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id} className="border-[#E2E8F0] hover:bg-transparent">
                {hg.headers.map((h) => (
                  <TableHead key={h.id} className="h-10 text-xs font-medium text-[#64748B]">
                    {h.isPlaceholder
                      ? null
                      : flexRender(h.column.columnDef.header, h.getContext())}
                  </TableHead>
                ))}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? "selected" : undefined}
                  className="border-[#F1F5F9] hover:bg-[#F8FAFC] data-[state=selected]:bg-[#F1F5F9]"
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-3">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow className="hover:bg-transparent">
                <TableCell
                  colSpan={columns.length}
                  className="h-24 text-center text-sm text-[#64748B]"
                >
                  {isLoading
                    ? t("common.loading", "Caricamento...")
                    : t("sources.noResults", "Nessuna fonte.")}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>

        {/* Pagine (dal server) */}
        <div className="flex items-center justify-between gap-3 border-t border-[#E2E8F0] px-4 py-2 text-xs text-[#64748B]">
          <span className="flex items-center gap-3">
            <span className="tabular-nums">
              {totalCount} {t("sources.resultsLabel", "fonti")}
            </span>
            {/* Quante per pagina: serve solo se non stanno tutte nella piu' piccola. */}
            {pageSizes.length > 1 && totalCount > pageSizes[0] && (
              <Select value={String(pageSize)} onValueChange={(v) => onPageSizeChange(Number(v))}>
                <SelectTrigger
                  aria-label={t("sources.perPage", "Per pagina")}
                  className="h-8 w-auto gap-1 rounded-[8px] border-black/10 bg-white px-2.5 text-xs text-[#334155] focus:ring-0 focus:ring-offset-0 focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {pageSizes.map((n) => (
                    <SelectItem key={n} value={String(n)} className="text-xs">
                      {t("sources.perPageOption", "{{count}} per pagina", { count: n })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </span>
          {pageCount > 1 ? (
            <span className="flex items-center gap-1">
              <span className="mr-2 tabular-nums">
                {t("sources.pageLabel", "Pagina")} {pageIndex + 1}{" "}
                {t("sources.ofLabel", "di")} {Math.max(1, pageCount)}
              </span>
              {[
                { icona: ChevronsLeft, pagina: 0, attivo: canPrev, etichetta: t("sources.firstPage", "Prima pagina") },
                { icona: ChevronLeft, pagina: pageIndex - 1, attivo: canPrev, etichetta: t("sources.previousPage", "Pagina precedente") },
                { icona: ChevronRight, pagina: pageIndex + 1, attivo: canNext, etichetta: t("sources.nextPage", "Pagina successiva") },
                { icona: ChevronsRight, pagina: pageCount - 1, attivo: canNext, etichetta: t("sources.lastPage", "Ultima pagina") },
              ].map(({ icona: Icona, pagina, attivo, etichetta }) => (
                <button
                  key={etichetta}
                  type="button"
                  aria-label={etichetta}
                  onClick={() => onPageChange(pagina)}
                  disabled={!attivo}
                  className="flex h-8 w-8 items-center justify-center rounded-[8px] hover:bg-[#F1F5F9] disabled:opacity-40"
                >
                  <Icona className="h-4 w-4" />
                </button>
              ))}
            </span>
          ) : null}
        </div>
      </div>

      {/* Read panel (modal Dialog) — read-only */}
      <Dialog
        open={!!viewSource}
        onOpenChange={(o) => {
          if (!o) setViewSource(null);
        }}
      >
        <DialogContent
          className={`max-h-[85vh] gap-0 overflow-y-auto rounded-[14px] p-0 ${
            viewSource?.type === "pdf" ? "sm:max-w-3xl" : "sm:max-w-lg"
          }`}
        >
          {viewSource && (
            <>
              <DialogHeader className="space-y-2 border-b pb-4 pl-6 pr-12 pt-8 text-left">
                <DialogTitle className="flex items-start gap-2 text-base font-semibold leading-snug">
                  <span className="mt-0.5 shrink-0">
                    {getTypeIcon(viewSource)}
                  </span>
                  <span className="break-words">
                    {getSourceDisplayName(viewSource)}
                  </span>
                </DialogTitle>
                <DialogDescription className="pl-7">
                  {getSourceSubtitle(viewSource)}
                </DialogDescription>
              </DialogHeader>
              <div className="px-6 pb-6 pt-5 text-sm">
                <SourceReadContent source={viewSource} t={t} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SourceReadContent({ source, t }) {
  if (source.type === "faq") {
    return (
      <div className="space-y-5">
        <div>
          <div className="mb-1.5 text-xs font-medium text-[#64748B]">
            {t("sources.question", "Domanda")}
          </div>
          <p className="font-medium leading-relaxed">{source.question}</p>
        </div>
        <div>
          <div className="mb-1.5 text-xs font-medium text-[#64748B]">
            {t("sources.answer", "Risposta")}
          </div>
          <p className="whitespace-pre-wrap leading-relaxed">{source.answer}</p>
        </div>
        {source.faq_origin === "crawl" && source.faq_origin_url && (
          <a
            href={source.faq_origin_url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 break-all text-xs font-medium text-[#18181B] underline-offset-2 hover:underline"
          >
            {t("sources.fromPage", "dalla pagina")}: {source.faq_origin_url}{" "}
            <ExternalLink className="h-3 w-3" />
          </a>
        )}
      </div>
    );
  }
  if (source.type === "product") {
    return (
      <div className="space-y-5">
        {source.product_image && (
          <img
            src={source.product_image}
            alt={source.product_name || "preview"}
            className="max-h-48 rounded-md border object-contain"
            onError={(e) => {
              e.currentTarget.style.display = "none";
            }}
          />
        )}
        <div>
          <div className="mb-1.5 text-xs font-medium text-[#64748B]">
            {t("sources.productName", "Nome")}
          </div>
          <p className="font-medium leading-relaxed">{source.product_name}</p>
        </div>
        {(source.product_description ||
          source.product_metadata?.description) && (
          <div>
            <div className="mb-1.5 text-xs font-medium text-[#64748B]">
              {t("sources.productDescription", "Descrizione")}
            </div>
            <p className="whitespace-pre-wrap leading-relaxed">
              {source.product_description ||
                source.product_metadata?.description}
            </p>
          </div>
        )}
        {source.product_price && (
          <div>
            <div className="mb-1.5 text-xs font-medium text-[#64748B]">
              {t("sources.productPrice", "Prezzo")}
            </div>
            <p className="font-medium">{source.product_price}</p>
          </div>
        )}
        {source.product_url && (
          <div>
            <div className="mb-1.5 text-xs font-medium text-[#64748B]">
              {t("sources.productUrl", "Link")}
            </div>
            <a
              href={source.product_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 break-all font-medium text-[#18181B] underline-offset-2 hover:underline"
            >
              {source.product_url} <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}
        {source.product_origin === "crawl" &&
          source.product_origin_url &&
          source.product_origin_url !== source.product_url && (
            <a
              href={source.product_origin_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 break-all text-xs font-medium text-[#18181B] underline-offset-2 hover:underline"
            >
              {t("sources.fromPage", "dalla pagina")}: {source.product_origin_url}{" "}
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
      </div>
    );
  }
  if (source.type === "text") {
    return <p className="whitespace-pre-wrap">{source.inline_text}</p>;
  }
  if (source.type === "url" || source.type === "sitemap") {
    return (
      <div className="space-y-2">
        <a
          href={source.url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 break-all font-medium text-[#18181B] underline-offset-2 hover:underline"
        >
          {source.url} <ExternalLink className="h-3 w-3" />
        </a>
        {source.metadata?.description && (
          <p className="text-muted-foreground">{source.metadata.description}</p>
        )}
      </div>
    );
  }
  if (source.type === "pdf") {
    return <PdfPreview source={source} t={t} />;
  }
  return <p className="text-muted-foreground">—</p>;
}

// PDF read view: fetch a short-lived signed URL and render it inline in the modal.
function PdfPreview({ source, t }) {
  const [url, setUrl] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setUrl(null);
    setError(false);
    api.sources
      .getViewUrl(source._id)
      .then((res) => {
        if (alive) setUrl(res?.url || null);
      })
      .catch(() => {
        if (alive) setError(true);
      });
    return () => {
      alive = false;
    };
  }, [source._id]);

  if (error) {
    return (
      <p className="text-muted-foreground">
        {t(
          "sources.openDocumentError",
          "Impossibile aprire il documento. Riprova.",
        )}
      </p>
    );
  }
  if (!url) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground">
        {t("common.loading", "Caricamento...")}
      </div>
    );
  }
  return (
    <div className="space-y-2">
      <iframe
        src={url}
        title={source.filename || "PDF"}
        className="h-[70vh] w-full rounded-md border"
      />
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-xs font-medium text-[#18181B] underline-offset-2 hover:underline"
      >
        {t("sources.openInNewTab", "Apri in una nuova scheda")}{" "}
        <ExternalLink className="h-3 w-3" />
      </a>
    </div>
  );
}
